import { Modal } from '@design/components/modal/Modal';
import { Spinner } from '@design/components/spinner/Spinner';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConditionsView } from './conditions-view/ConditionsView';
import { ErrorView } from './error-view/ErrorView';
import { QuestionView } from './question-view/QuestionView';
import styles from './ScenarioAssistantModal.module.scss';
import { useScenarioAssistant } from './use-scenario-assistant/useScenarioAssistant';

export interface ScenarioAssistantModalProps {
  onClose: () => void;
  isOpen: boolean;
  editFilterKey?: string;
  resetEditFilterKey: () => void;
}

export const ScenarioAssistantModal = ({
  onClose,
  isOpen,
  editFilterKey,
  resetEditFilterKey,
}: ScenarioAssistantModalProps) => {
  const { t } = useTranslation(['screener', 'scenarioCommander']);
  const {
    askedQuestion,
    error,
    loading,
    conditions,
    editingConditionId,
    editQuestion,
    resetAskedQuestions,
    abortCurrentRequest,
  } = useScenarioAssistant(state => ({
    askedQuestion: state.askedQuestion,
    error: state.error,
    loading: state.loading,
    conditions: state.conditions,
    editingConditionId: state.editingConditionId,
    editQuestion: state.editQuestion,
    resetAskedQuestions: state.resetAskedQuestions,
    abortCurrentRequest: state.abortCurrentRequest,
  }));
  const [question, setQuestion] = useState('');

  const handleSetQuestion = (question: string) => {
    setQuestion(question);
  };

  const handleEdit = () => {
    const conditionIdToEdit =
      editFilterKey || conditions[conditions.length - 1]?.id;

    if (editFilterKey || !!conditions.length) {
      editQuestion(conditionIdToEdit);
    }

    resetAskedQuestions();

    const conditionToEdit = conditions.find(c => c.id === conditionIdToEdit);
    handleSetQuestion(conditionToEdit?.askedQuestion ?? askedQuestion);

    resetEditFilterKey();
  };

  const handleClose = () => {
    if (isOpen) {
      setQuestion('');
    }

    abortCurrentRequest();
    onClose();
    editQuestion(undefined);
  };

  // show when editing current condition (clicked on 'screener:editTextEntry')
  // or when initially opened
  const showQuestionView =
    editingConditionId || (!editFilterKey && !askedQuestion);

  return (
    <Modal
      className={styles.styledModalRoot}
      data-testid="scenario-assistant-modal"
      isOpen={isOpen}
      title={t('screener:modal.scenarioAssistant')}
      description={t('screener:modal.scenarioDescription')}
      onClose={handleClose}
      outsideClickClose={!conditions.length}
    >
      {loading && (
        <div className={styles.spinnerWrapper} data-testid="scenario-question-loading">
          <Spinner size="large" />
        </div>
      )}
      {error && <ErrorView handleClose={handleClose} handleEdit={handleEdit} />}

      {!error && !loading && (
        <>
          {showQuestionView ? (
            <QuestionView
              question={question}
              handleSetQuestion={handleSetQuestion}
              handleClose={handleClose}
            />
          ) : (
            <ConditionsView
              handleEdit={handleEdit}
              handleClose={onClose}
              editFilterKey={editFilterKey}
            />
          )}
        </>
      )}
    </Modal>
  );
};
